// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Alert, TextField, MenuItem, CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';

// THE TRASH.
//
// 012_soft_delete.sql made deleting a fact sheet reversible and 014 did the same
// for documents. Neither is worth anything without somewhere to see what is in
// the trash and put it back — a soft delete with no restore screen is just
// hidden data plus a storage bill.
//
// Who may do what, and it is not the same answer for both halves:
//   see the trash  -> anyone who can administer the workspace. It is their own
//                     estate and Row-Level Security bounds a client user to it
//                     whatever this screen asks for.
//   restore        -> Optimum master only, said by the API and again by the
//                     RESTRICTIVE policies in the database. The buttons are
//                     hidden for everybody else, but that is courtesy: the
//                     refusal is server-side.
//
// Nothing here can purge. The 30-day sweep is purge_deleted_fact_sheets /
// purge_deleted_attachments in the database, called by a scheduled job — there
// is deliberately no button that empties a client's trash on demand.

const fmt = (t) => {
  if (!t) return '—';
  try { return new Date(t).toLocaleString(); } catch (e) { return String(t); }
};

// Days left before the sweep takes it. The retention lives in the database
// function's default; this is the same number written down for a human, and it
// is the one thing on this screen that would need changing alongside it.
const RETENTION_DAYS = 30;
const daysLeft = (deletedAt) => {
  const at = Date.parse(deletedAt || '');
  if (Number.isNaN(at)) return null;
  return Math.max(0, RETENTION_DAYS - Math.floor((Date.now() - at) / 86400000));
};

export default function TrashAdmin({ tenants = [], tenantId }) {
  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');

  // A master must name a workspace: their unscoped estate is every client at
  // once, which is more than one response can carry — the same reason
  // /factsheets refuses an unscoped list for a master.
  const clientTenants = useMemo(
    () => tenants.filter((t) => t.id !== 'optimum'), [tenants],
  );
  const [scope, setScope] = useState(() => {
    if (!iAmMaster) return (me && me.tenantId) || '';
    if (tenantId && tenantId !== 'optimum') return tenantId;
    return '';
  });

  const [sheets, setSheets] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // CloudFront turns an API 403/404 into 200 plus the SPA shell, so a refusal
    // arrives as HTML that JSON.parse chokes on. Report the status rather than
    // "Unexpected token <".
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    if (iAmMaster && !scope) { setSheets([]); setDocs([]); return; }
    setLoading(true); setErr('');
    try {
      const page = await api(`/factsheets/trash${scope ? `?tenantId=${encodeURIComponent(scope)}` : ''}`);
      setSheets(Array.isArray(page) ? page : (page.items || []));
    } catch (e) { setErr(e.message); setSheets([]); }
    // Documents are master-only to even list, so a client admin gets the fact
    // sheets and no error about the half they were never offered.
    if (iAmMaster) {
      try {
        const rows = await api(`/documents?deleted=1${scope ? `&tenantId=${encodeURIComponent(scope)}` : ''}`);
        setDocs(Array.isArray(rows) ? rows : []);
      } catch (e) { setDocs([]); }
    }
    setLoading(false);
  }, [api, scope, iAmMaster]);

  useEffect(() => { load(); }, [load]);

  const restoreSheet = async (row) => {
    setErr(''); setMsg('');
    try {
      await api('/factsheet/restore', { method: 'POST', body: JSON.stringify({ id: row.id }) });
      setMsg(`Restored "${row.name}". Reload the workspace to see it in the inventory.`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const restoreDoc = async (row) => {
    setErr(''); setMsg('');
    try {
      await api('/documents/restore', { method: 'POST', body: JSON.stringify({ id: row.id }) });
      setMsg(`Restored "${row.title}".`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Deleted records</Typography>
        <Chip size="small" label={`kept for ${RETENTION_DAYS} days`} />
        {iAmMaster && (
          <TextField select size="small" label="Workspace" value={scope}
            onChange={(e) => setScope(e.target.value)} sx={{ minWidth: 260 }}>
            {clientTenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
      </Stack>

      {!iAmMaster && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Deleted records are kept for {RETENTION_DAYS} days. Ask Optimum to put one back — restoring
          is deliberately not something a client account can do to its own history.
        </Alert>
      )}
      {iAmMaster && !scope && (
        <Alert severity="info" sx={{ mb: 2 }}>Choose a workspace to see what is in its trash.</Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Typography sx={{ p: 1.5, fontWeight: 700, color: 'sectionHeader.main', fontSize: 14 }}>
              Fact sheets ({sheets.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Deleted</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Purged in</TableCell>
                  {iAmMaster && <TableCell align="right">Restore</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sheets.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell>{fmt(r.deletedAt)}</TableCell>
                    <TableCell>{r.deletedBy || '—'}</TableCell>
                    <TableCell>
                      {daysLeft(r.deletedAt) === null ? '—' : `${daysLeft(r.deletedAt)} day(s)`}
                    </TableCell>
                    {iAmMaster && (
                      <TableCell align="right">
                        <Tooltip title="Restore this record">
                          <IconButton size="small" onClick={() => restoreSheet(r)}><RestoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!sheets.length && (
                  <TableRow>
                    <TableCell colSpan={iAmMaster ? 6 : 5} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                      Nothing in the trash.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {iAmMaster && (
            <Paper>
              <Typography sx={{ p: 1.5, fontWeight: 700, color: 'sectionHeader.main', fontSize: 14 }}>
                Documents ({docs.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                    <TableCell>Title</TableCell>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Deleted</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Purged in</TableCell>
                    <TableCell align="right">Restore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docs.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.title}</TableCell>
                      <TableCell>{r.tenant_id || 'base library'}</TableCell>
                      <TableCell>{fmt(r.deleted_at)}</TableCell>
                      <TableCell>{r.deleted_by || '—'}</TableCell>
                      <TableCell>
                        {daysLeft(r.deleted_at) === null ? '—' : `${daysLeft(r.deleted_at)} day(s)`}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Restore this document">
                          <IconButton size="small" onClick={() => restoreDoc(r)}><RestoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!docs.length && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                        No deleted documents.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography sx={{ p: 1.5, fontSize: 12.5, color: '#5C6B7A' }}>
                The file itself is left in storage until the sweep, so a restore here brings back a
                document you can actually open rather than a row pointing at nothing.
              </Typography>
            </Paper>
          )}
        </>
      )}

      <Button sx={{ mt: 2 }} size="small" onClick={load} startIcon={<RefreshIcon />}>Refresh</Button>
    </Box>
  );
}
